'use client';

import { ArrowUpRight, BarChart3, Bookmark, Heart, MessageCircle, Quote, Repeat2 } from 'lucide-react';
import { VerifiedBadge } from '@/components/shared/VerifiedBadge';
import { Avatar } from '@/components/ui/avatar';
import { formatBytes } from '@/lib/formatters/bytes';
import { formatNumber } from '@/lib/utils';
import { formatDateTime, formatTimeAgo } from '@/lib/utils/date';
import { stripHandlePrefixes } from '@/lib/utils/regex';
import { toTrimmedNonEmptyString } from '@/lib/utils/type-guards';
import type {
  ArchiveDataset,
  ArchiveToolResultDigest,
  ArchiveTweetSummaryData,
  ArchiveTweetToolResultData,
} from '../types';
import { pluralize } from '../utils';
import { EmptyResult } from './GenericObjectResult';

const DATASET_LABELS: Record<ArchiveDataset, { header: string; empty: string; actionVerb: string }> = {
  tweets: {
    header: 'your tweets',
    empty: 'No tweets from your archive are available yet.',
    actionVerb: 'Posted',
  },
  mentions: {
    header: 'your mentions',
    empty: 'No mentions from your archive are available yet.',
    actionVerb: 'Mentioned',
  },
  likes: {
    header: 'your liked tweets',
    empty: 'No liked tweets were found in your archive yet.',
    actionVerb: 'Liked',
  },
  bookmarks: {
    header: 'your bookmarked tweets',
    empty: 'No bookmarked tweets were found in your archive yet.',
    actionVerb: 'Bookmarked',
  },
};

export interface ArchiveTweetsResultProperties {
  result: ArchiveTweetToolResultData;
}

export function ArchiveTweetsResult({ result }: ArchiveTweetsResultProperties) {
  const meta = DATASET_LABELS[result.dataset];
  const { applied, max, requested, recommendedNext } = result.limits;
  const totalItems = typeof result.totalItems === 'number' ? result.totalItems : result.items.length;

  if (result.items.length === 0) {
    if (result.digest) {
      return <ArchiveDigestNotice datasetLabel={meta.header} totalItems={totalItems} digest={result.digest} />;
    }
    return <EmptyResult message={meta.empty} />;
  }

  return (
    <div className="space-y-3 text-sm">
      <div className="text-xs text-muted-foreground">
        Showing {pluralize(result.items.length, 'tweet')} from {meta.header} (limit {applied}
        {requested && requested !== applied ? ` from requested ${requested}` : ''}; max {max} per call).
      </div>
      {recommendedNext > applied ? (
        <div className="text-xs text-muted-foreground/80">
          Ask for about {recommendedNext} items next to speed things up, or set a custom limit up to {max}.
        </div>
      ) : null}
      <div className="space-y-3">
        {result.items.map((item) => (
          <ArchiveTweetCard key={item.id} summary={item} />
        ))}
      </div>
      {result.pagination.hasMore ? (
        <div className="text-xs text-muted-foreground/80">Ask for more to load older {meta.header}.</div>
      ) : null}
    </div>
  );
}

function ArchiveTweetCard({ summary }: { summary: ArchiveTweetSummaryData }) {
  const createdAt = summary.createdAt ?? null;
  const actionAt = summary.sourceTimestamp ?? null;
  const sanitizedHandle = summary.author?.username ? toTrimmedNonEmptyString(summary.author.username) : null;
  const normalizedHandle = sanitizedHandle ? stripHandlePrefixes(sanitizedHandle) : null;
  const username = normalizedHandle ? `@${normalizedHandle}` : null;
  const authorName = toTrimmedNonEmptyString(summary.author?.name) ?? username ?? 'Tweet';
  const verifiedType =
    summary.author?.verifiedType && summary.author.verifiedType !== 'none' ? summary.author.verifiedType : null;

  const metrics = buildMetrics(summary);

  return (
    <div className="rounded-lg border border-border/70 bg-card/70 p-3 shadow-sm">
      <div className="flex items-start gap-3">
        <Avatar
          src={summary.author?.profileImageUrl}
          alt={authorName}
          size="sm"
          fallbackText={summary.author?.name ?? sanitizedHandle ?? 'Tweet'}
          className="mt-1"
        />
        <div className="flex-1 space-y-2">
          <header className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
            <span>{authorName}</span>
            {verifiedType ? <VerifiedBadge type={verifiedType} size="sm" /> : null}
          </header>
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            {username ? <span>{username}</span> : null}
            {createdAt ? <span title={formatDateTime(createdAt)}>Posted {formatTimeAgo(createdAt)}</span> : null}
            {actionAt && summary.dataset !== 'tweets' ? (
              <span title={formatDateTime(actionAt)}>
                {DATASET_LABELS[summary.dataset].actionVerb} {formatTimeAgo(actionAt)}
              </span>
            ) : null}
          </div>
          <div className="whitespace-pre-wrap text-sm text-foreground">{summary.text}</div>
          {metrics.length > 0 ? (
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
              {metrics.map(({ label, icon: Icon, value }) => (
                <span key={label} className="inline-flex items-center gap-1">
                  <Icon className="h-3.5 w-3.5" />
                  <span className="font-medium text-foreground">{formatNumber(value)}</span>
                  <span>{label}</span>
                </span>
              ))}
            </div>
          ) : null}
          <div className="text-xs">
            <a
              href={`https://x.com/i/web/status/${summary.id}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
            >
              View on X
              <ArrowUpRight className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function ArchiveDigestNotice({
  datasetLabel,
  totalItems,
  digest,
}: {
  datasetLabel: string;
  totalItems: number;
  digest: ArchiveToolResultDigest;
}) {
  const safeCount = Number.isFinite(totalItems) ? Math.max(Math.round(totalItems), 0) : 0;
  const expiresAt = new Date(digest.expiresAt);
  return (
    <div className="space-y-3 rounded-lg border border-border/70 bg-card/70 p-4 text-sm text-muted-foreground shadow-sm">
      <p>
        We cached {pluralize(safeCount, 'tweet')} from {datasetLabel} to keep this analysis responsive. You can ask for
        targeted follow-ups, or retrieve the full snapshot directly if you need raw data.
      </p>
      <div className="space-y-1 text-xs leading-5 text-muted-foreground">
        <div>
          Call{' '}
          <code className="rounded bg-muted px-2 py-1 font-mono text-xs text-foreground">
            GET /api/chat/tool-result/{digest.cacheToken}
          </code>{' '}
          before it expires to download the JSON payload.
        </div>
        <div>
          Expires {formatDateTime(expiresAt)} · Payload size {formatByteEstimate(digest.byteSize)} · Tool call{' '}
          <code className="font-mono text-[11px] text-foreground/80">{digest.toolCallId}</code>
        </div>
      </div>
    </div>
  );
}

function formatByteEstimate(value: number | null): string {
  return formatBytes(value, { fallback: 'unknown' });
}

function buildMetrics(summary: ArchiveTweetSummaryData) {
  type MetricDescriptor = { label: string; icon: typeof Heart; value: number | null | undefined };

  const metrics: MetricDescriptor[] = [
    { label: 'Replies', icon: MessageCircle, value: summary.metrics.replies },
    { label: 'Retweets', icon: Repeat2, value: summary.metrics.retweets },
    { label: 'Likes', icon: Heart, value: summary.metrics.likes },
    { label: 'Quotes', icon: Quote, value: summary.metrics.quotes },
    { label: 'Bookmarks', icon: Bookmark, value: summary.metrics.bookmarks },
    { label: 'Views', icon: BarChart3, value: summary.metrics.impressions },
  ];
  return metrics.filter(
    (metric): metric is MetricDescriptor & { value: number } =>
      typeof metric.value === 'number' && Number.isFinite(metric.value) && metric.value >= 0
  );
}
